import { Permission } from '../tenant_portal/layout/ContextProvider';
import type { CountryContext } from './AddressContainer';
import { OauthProvider, permissionsPageNames } from './Constants';
import { CustomFieldContent, ErrorType } from './Types';
import GoogleLogoPath from '../../../images/login/google.svg';
import AppleLogoPath from '../../../images/login/apple.svg';
import FacebookLogoPath from '../../../images/login/facebook.svg';
import MicrosoftLogoPath from '../../../images/login/microsoft.svg';
import EmailLogoPath from '../../../images/login/email.svg';
import { graphql } from 'relay-runtime';
import { useMutation } from 'react-relay';
import { FREE_TRIAL_NAVBAR_ROUTE_STRIP_SEGMENTS } from './free_trial/Constants';

export const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const formatCurrencyString = (
  number: number | string,
  includeDecimals = true
): string => {
  if (typeof number === 'string') number = parseFloat(number);
  const currency = number.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: includeDecimals ? 2 : 0,
  });
  if (currency === '-$0') return '$0';
  return currency;
};

export const parseCurrencyString = (currency: string): number => {
  if (!currency) return 0;
  const cleaned = currency.replace(/[$,]/g, '');
  return parseFloat(cleaned) || 0;
};

// Should match config/initializers/global_constants.rb
export enum DateFormat {
  UI_DATE = 'UIDateFormat',
  UI_DATE_STRING = 'UIDateStringFormat',
}

const UI_DATE_INPUT_REGEX = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/;
const ISO_DATE_INPUT_REGEX = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:T.*)?$/;

const parseISODateOnlyString = (dateString: string) => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

const formatValidatedUIDate = (year: number, month: number, day: number) => {
  const normalizedYear = year < 100 ? year + 2000 : year;
  const date = new Date(Date.UTC(normalizedYear, month - 1, day));
  const isValidDate =
    date.getUTCFullYear() === normalizedYear &&
    date.getUTCMonth() + 1 === month &&
    date.getUTCDate() === day;

  return isValidDate ? `${month}/${day}/${normalizedYear}` : '';
};

interface FormatUIDateStringOptions {
  treatAsDateOnly?: boolean;
}

export const formatUIDateString = (
  dateString: string,
  dateFormat: DateFormat = DateFormat.UI_DATE,
  { treatAsDateOnly = false }: FormatUIDateStringOptions = {}
): string => {
  if (!dateString) return '';
  const date = treatAsDateOnly
    ? parseISODateOnlyString(dateString)
    : new Date(dateString);

  switch (dateFormat) {
    case DateFormat.UI_DATE: {
      const day = date.getUTCDate();
      const month = date.getUTCMonth() + 1; // Months are zero based
      const year = date.getUTCFullYear();
      return `${month}/${day}/${year}`;
    }
    case DateFormat.UI_DATE_STRING: {
      return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: treatAsDateOnly ? 'UTC' : undefined,
      });
    }
    default:
      return date.toDateString();
  }
};

export const normalizeUIDateInput = (dateString: string): string => {
  if (!dateString) return '';

  const trimmedDateString = dateString.trim();
  if (!trimmedDateString) return '';

  const uiDateMatch = trimmedDateString.match(UI_DATE_INPUT_REGEX);
  const isoDateMatch = trimmedDateString.match(ISO_DATE_INPUT_REGEX);
  const dateMatch = uiDateMatch || isoDateMatch;

  if (!dateMatch) return '';

  const [year, month, day] = isoDateMatch
    ? [dateMatch[1], dateMatch[2], dateMatch[3]].map(Number)
    : [dateMatch[3], dateMatch[1], dateMatch[2]].map(Number);

  return formatValidatedUIDate(year, month, day);
};

export const parseUIDateString = (
  dateStr: string,
  dateFormat: DateFormat = DateFormat.UI_DATE
): Date => {
  switch (dateFormat) {
    case DateFormat.UI_DATE: {
      const parts = dateStr.split('/');
      const month = parseInt(parts[0]) - 1;
      const day = parseInt(parts[1]);
      let year = parseInt(parts[2]);
      if (year < 100) {
        year += 2000;
      }
      return new Date(year, month, day);
    }
    default:
      return new Date(dateStr);
  }
};

export const formatUIDatetimeString = (
  datetimeString: string,
  timeZone?: string
): string => {
  const date = new Date(datetimeString);

  const dateFormatter = new Intl.DateTimeFormat(undefined, { timeZone });
  const formattedDate = dateFormatter.format(date);

  const timeFormatter = new Intl.DateTimeFormat(undefined, {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  const formattedTime = timeFormatter.format(date);

  return `${formattedDate} ${formattedTime}`;
};

export const getBrowserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
};

export const formatCreditCardExpDate = (dateString: string): string => {
  const date = new Date(dateString);
  const month = date.getUTCMonth() + 1; // Months are zero based
  const year = date.getUTCFullYear().toString().slice(-2);

  return `${month}/${year}`;
};

export const getNextAutoPaymentDate = (day = new Date().getDate()): string => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const nextInstance = new Date(today.getFullYear(), today.getMonth(), day);

  if (nextInstance.getDate() == day) {
    if (nextInstance <= today) {
      nextInstance.setMonth(today.getMonth() + 1, day);
    }
  } else {
    nextInstance.setMonth(today.getMonth() + 1, 0);

    if (nextInstance < today) {
      nextInstance.setMonth(today.getMonth() + 2, 0);
    }
  }
  return nextInstance.toLocaleDateString();
};

export const firstAllowedPage = (leasePermissions: Permission) => {
  if (leasePermissions[permissionsPageNames.PAYMENTS]) {
    return 'payments';
  } else if (leasePermissions[permissionsPageNames.MAINTENANCE]) {
    return 'maintenance';
  } else if (leasePermissions[permissionsPageNames.DOCUMENTS]) {
    return 'documents';
  } else if (leasePermissions[permissionsPageNames.SUPPORT]) {
    return 'support';
  } else {
    return 'account';
  }
};

export const replaceZipWithPostal = (
  label: string,
  country: CountryContext['country']
): string => {
  if (country === 'USA') {
    return label;
  } else {
    return label
      .replace('Zip Code', 'Postal Code')
      .replace('Zip', 'Postal Code');
  }
};

export const formatErrorMessages = (
  errors: ErrorType[],
  label: string,
  usePathLabels = false
) => {
  const formattedMessages = errors
    .map((error) => {
      if (usePathLabels) {
        label = error.path === 'base' ? '' : error.path;
      }
      return error.messages.map((message) => {
        return `${label !== '' ? `${label} ${message}.` : message} `;
      });
    })
    .flat()
    .join(' ');
  return formattedMessages;
};

export const toCamelCase = (str: string) => {
  return str
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) => {
      if (/\s+/.test(match)) return '';
      return index === 0 ? match.toLowerCase() : match.toUpperCase();
    });
};

export const camelCaseToTitle = (str: string) => {
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b[a-z]/g, (char) => char.toUpperCase());
};

type NormalizeNavbarRouteBaseOptions = {
  entityId?: string;
  merchantId?: string;
  stripSegments?: string[];
};

export const normalizeNavbarRouteBase = (
  pathname: string,
  {
    entityId,
    merchantId,
    stripSegments = ['inbox', 'scans'],
  }: NormalizeNavbarRouteBaseOptions = {}
) => {
  const trimmedPath = pathname.replace(/\/$/, '');
  let pathSegments = trimmedPath.split('/').filter(Boolean);
  const idToRemove = entityId || merchantId;
  const stripSegmentSet = new Set(stripSegments);
  const isMarketingLeadsPath =
    pathSegments[0] === 'marketing' && pathSegments[1] === 'leads';
  const isValidMarketingLeadId =
    !!idToRemove &&
    isMarketingLeadsPath &&
    !stripSegmentSet.has(idToRemove) &&
    pathSegments.includes(idToRemove);

  if (idToRemove) {
    const idIndex = pathSegments.indexOf(idToRemove);
    if (idIndex !== -1) {
      pathSegments = pathSegments.slice(
        0,
        isValidMarketingLeadId ? idIndex + 1 : idIndex
      );
    }
  }

  const stripIndex = pathSegments.findIndex((segment) =>
    stripSegmentSet.has(segment)
  );
  if (stripIndex !== -1) {
    pathSegments = pathSegments.slice(0, stripIndex);
  }

  if (pathSegments.length === 0) {
    return '/';
  }

  const baseSegmentCounts: Record<string, number> = {
    accounting: 2,
    marketing: 2,
    maintenance: 1,
    reports: 1,
    settings: 1,
  };
  const marketingLeadMaxSegments = isValidMarketingLeadId ? 3 : null;
  const defaultMaxSegments = baseSegmentCounts[pathSegments[0]];
  const maxSegments = marketingLeadMaxSegments || defaultMaxSegments;

  if (maxSegments && pathSegments.length > maxSegments) {
    pathSegments = pathSegments.slice(0, maxSegments);
  }

  return `/${pathSegments.join('/')}`;
};

type BackgroundPathFromLocationOptions = {
  entityId?: string;
  location: { pathname: string };
  merchantId?: string;
  stripSegments?: string[];
};

export const backgroundPathFromLocation = ({
  entityId,
  location,
  merchantId,
  stripSegments = FREE_TRIAL_NAVBAR_ROUTE_STRIP_SEGMENTS,
}: BackgroundPathFromLocationOptions) => {
  const normalizedCurrentPath = normalizeNavbarRouteBase(location.pathname, {
    entityId,
    merchantId,
    stripSegments,
  });

  return normalizedCurrentPath === '/' ? '/properties' : normalizedCurrentPath;
};

export const getSystemTheme = () => {
  let theme = 'standard';
  if (
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  ) {
    theme = 'dark';
  }
  return theme;
};

export const formatProviderName = (provider: string) => {
  const providerName = Object.entries(OauthProvider).find(([, value]) => {
    return value === provider;
  })?.[0];
  if (!providerName) return provider;
  return (
    providerName.charAt(0).toUpperCase() + providerName.slice(1).toLowerCase()
  );
};

export const getProviderLogoPath = (provider: string) => {
  let path = '';
  switch (provider) {
    case OauthProvider.GOOGLE:
      path = GoogleLogoPath;
      break;
    case OauthProvider.APPLE:
      path = AppleLogoPath;
      break;
    case OauthProvider.FACEBOOK:
      path = FacebookLogoPath;
      break;
    case OauthProvider.MICROSOFT:
      path = MicrosoftLogoPath;
      break;
    case OauthProvider.EMAIL:
      path = EmailLogoPath;
      break;
  }
  return path;
};

const SendErrorReport = graphql`
  mutation ToolsSendErrorReportMutation($input: ToolsSendErrorReportInput!) {
    sendErrorReport(input: $input) {
      errors {
        path
        messages
      }
    }
  }
`;

export function useSendErrorReport() {
  const [sendErrorReport] = useMutation(SendErrorReport);

  const submitErrorReport = (subject: string, body: string) => {
    sendErrorReport({
      variables: {
        input: {
          errorReportInfo: {
            subject: subject,
            body: body,
          },
        },
      },
    });
  };

  return submitErrorReport;
}

export function getNetworkErrorMessage(
  actionError: string,
  networkError: Error
) {
  if (networkError.message.includes('Received status code 500'))
    actionError +=
      '. Please contact Property Matrix support for assistance (Code 500).';
  else actionError += `: ${networkError.message}`;
  return actionError;
}

export function getCustomFieldValue(content: CustomFieldContent) {
  return (
    content.checkboxValue ||
    content.currencyValue ||
    content.dateValue ||
    content.dateTimeValue ||
    content.memoValue ||
    content.numberValue ||
    content.selectValue ||
    content.textValue ||
    content.timeValue
  );
}

export function getRailsIdFromRelayId(relayId: string) {
  return atob(relayId).split('/').pop();
}
export function getRelayIdFromRailsId(modelName: string, railsId: string) {
  return btoa(`gid://property-matrix/${modelName}/${railsId}`).replace(
    /=+$/,
    ''
  );
}

export function rechartsCurrencyFormatter(value: string) {
  const number = parseFloat(value);
  return number.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function hexToRgb(hex: string) {
  let r = 0,
    g = 0,
    b = 0;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex[1] + hex[2], 16);
    g = parseInt(hex[3] + hex[4], 16);
    b = parseInt(hex[5] + hex[6], 16);
  }
  return [r, g, b];
}

function rgbToHex(r: number, g: number, b: number) {
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

export function lightenColor(color: string, percent: number) {
  const [r, g, b] = hexToRgb(color);
  const newR = Math.min(255, Math.floor(r + (255 - r) * percent));
  const newG = Math.min(255, Math.floor(g + (255 - g) * percent));
  const newB = Math.min(255, Math.floor(b + (255 - b) * percent));
  return rgbToHex(newR, newG, newB);
}

export function getWidgetDimensionLimits(widgetType: string) {
  let minWidth = 2;
  let minHeight = 2;
  let maxWidth = 4;
  let maxHeight = 4;
  switch (widgetType) {
    case 'DashboardWidgetsIncomeVsExpense':
      minWidth = 5;
      minHeight = 2;
      maxWidth = 12;
      maxHeight = 4;
      break;
    case 'DashboardWidgetsLeadsByLabel':
      minWidth = 3;
      minHeight = 2;
      maxWidth = 6;
      maxHeight = 4;
      break;
    case 'DashboardWidgetsTotalRevenue':
      minWidth = 3;
      minHeight = 1;
      maxWidth = 6;
      maxHeight = 4;
      break;
    case 'DashboardWidgetsTotalOutstandingBalance':
      minWidth = 2;
      minHeight = 1;
      maxWidth = 6;
      maxHeight = 4;
      break;
    case 'DashboardWidgetsIncomeByProperty':
      minWidth = 3;
      minHeight = 2;
      maxWidth = 6;
      maxHeight = 4;
      break;
    case 'DashboardWidgetsUpcomingLeaseExpiration':
      minWidth = 3;
      minHeight = 2;
      maxWidth = 6;
      maxHeight = 4;
      break;
    case 'DashboardWidgetsOccupancyRate':
      minWidth = 3;
      minHeight = 1;
      maxWidth = 6;
      maxHeight = 4;
      break;
    case 'DashboardWidgetsPayments':
      minWidth = 2;
      minHeight = 2;
      maxWidth = 6;
      maxHeight = 4;
      break;
    case 'DashboardWidgetsExpenseByProperty':
      minWidth = 3;
      minHeight = 2;
      maxWidth = 6;
      maxHeight = 4;
      break;
    case 'DashboardWidgetsOutstandingBalances':
      minWidth = 5;
      minHeight = 2;
      maxWidth = 12;
      maxHeight = 4;
      break;
    case 'DashboardWidgetsMaintenanceRequestsByCategoryAndStatus':
      minWidth = 3;
      minHeight = 2;
      maxWidth = 6;
      maxHeight = 4;
      break;
  }
  return {
    minWidth: minWidth,
    minHeight: minHeight,
    maxWidth: maxWidth,
    maxHeight: maxHeight,
  };
}

export function filterErrorsByPath(
  errors: ErrorType[],
  paths: string[] = ['base'],
  include = true
) {
  const seen = new Set<string>();

  return errors.flatMap((error) => {
    const lastPart = error.path.split('.').slice(-1)[0] || '';
    const matches = include
      ? paths.includes(lastPart)
      : !paths.includes(lastPart);
    if (!matches) return [];

    const uniqueMessages = error.messages.filter((message) => {
      const key = `${lastPart}::${message}`;
      if (seen.has(key)) return false;

      seen.add(key);
      return true;
    });

    return uniqueMessages.length > 0
      ? [{ ...error, messages: uniqueMessages }]
      : [];
  });
}

export const itemMatcher = (params: unknown, data: unknown): unknown => {
  const typedParams = params as { term?: string };
  const typedData = data as {
    children?: unknown[];
    text?: string;
    element?: Element;
  };

  if (!typedParams || !typedParams.term || typedParams.term.trim() === '') {
    return data;
  }

  if (!typedData) {
    return null;
  }

  // Normal Select2 match check
  if (typedData.children && typedData.children.length > 0) {
    const filteredChildren = typedData.children
      .map((child: unknown) => itemMatcher(params, child))
      .filter((child: unknown) => child !== null);

    if (filteredChildren.length > 0) {
      return {
        ...typedData,
        children: filteredChildren,
      };
    }
    return null;
  }

  const searchTerm = typedParams.term.toLowerCase();
  const itemText = typedData.text ? typedData.text.toLowerCase() : '';

  if (itemText.includes(searchTerm)) {
    return data;
  }

  // Check if this item should match based on account hierarchy
  const fullAccountName = typedData.element
    ? typedData.element.getAttribute('data-full-account-name')
    : null;
  if (fullAccountName) {
    const currentAccountParts = fullAccountName
      .toLowerCase()
      .split(':')
      .map((part: string) => part.trim());

    const select = typedData.element?.closest('select');
    if (!select) {
      return null;
    }

    const allOptions = Array.from(
      select.querySelectorAll('option')
    ) as HTMLOptionElement[];

    const hasMatchingParent = allOptions.some((option: HTMLOptionElement) => {
      const optionText = option.textContent
        ? option.textContent.toLowerCase()
        : '';
      const optionAccountName = option.getAttribute('data-full-account-name');

      if (optionAccountName && optionText.includes(searchTerm)) {
        const optionAccountParts = optionAccountName
          .toLowerCase()
          .split(':')
          .map((part: string) => part.trim());

        if (optionAccountParts.length < currentAccountParts.length) {
          return optionAccountParts.every(
            (part: string, index: number) => currentAccountParts[index] === part
          );
        }
      }
      return false;
    });

    if (hasMatchingParent) {
      return data;
    }
  }

  return null;
};
